/**
 * propagate-entity — stable seam over rescanUnmatchedForEntity.
 *
 * This thin wrapper gives us a single call-site for "given a source
 * transaction that just received an entity link, which other uncategorized
 * transactions in this household match the same entity?" and shields the rest
 * of the codebase from direct dependency on categorization-client internals.
 *
 * The caller is responsible for:
 *  - Adding the new alias to `bag` before calling (so the alias is visible
 *    to the resolver).
 *  - Excluding isTransfer and isIgnored rows from `input.unmatched`.
 */

import { defaultCategorySlug } from "@paisa/reconciliation"
import {
  type RescanInput,
  type RescanResult,
  rescanUnmatchedForEntity,
} from "./categorization-client"

export type { RescanInput, RescanResult }

// ── Shared context-building types ────────────────────────────────────────────

export interface ResolveContextExtras {
  globalMerchants: Array<{ id: string; name: string }>
  localMerchants: Array<{ id: string; name: string }>
  persons: Array<{ id: string; name: string; upiHandles?: string[] }>
  /** Merchant id → its built-in default category, used when the household has no rule yet. */
  merchantCategoryById: Record<string, string>
}

interface MerchantLike {
  id: string
  cleanName: string | null
  householdId?: string | null
  categoryId?: string | null
}

interface CategoryLike {
  id: string
  slug: string
}

interface PersonLike {
  id: string
  name: string
  upiHandles?: string[]
}

/**
 * Build the `ctxExtras` argument needed by `computePropagation` and
 * `autoCategorizeFromDescriptions` from raw merchants/persons API responses.
 *
 * Filtering rules (identical to what both consumers previously did inline):
 *  - `globalMerchants` — merchants with no `householdId` that have a `cleanName`.
 *  - `localMerchants`  — merchants WITH a `householdId` that have a `cleanName`.
 *  - `persons`         — all persons mapped to `{ id, name }`.
 */
export function buildResolveContextExtras(
  merchants: MerchantLike[],
  persons: PersonLike[],
  categories: CategoryLike[] = [],
): ResolveContextExtras {
  const categoryIdBySlug = new Map(categories.map((c) => [c.slug, c.id]))
  const ownCategoryIds = new Set(categories.map((c) => c.id))
  return {
    globalMerchants: merchants
      .filter((m) => !m.householdId && m.cleanName)
      .map((m) => ({ id: m.id, name: m.cleanName as string })),
    localMerchants: merchants
      .filter((m) => m.householdId && m.cleanName)
      .map((m) => ({ id: m.id, name: m.cleanName as string })),
    persons: persons.map((p) => ({ id: p.id, name: p.name, upiHandles: p.upiHandles })),
    merchantCategoryById: Object.fromEntries(
      merchants.flatMap((m) => {
        // The merchant's own category if it's one of this household's; else the
        // built-in default for that merchant, matched to the household's category by slug.
        if (m.categoryId && ownCategoryIds.has(m.categoryId)) return [[m.id, m.categoryId]]
        const slug = m.cleanName ? defaultCategorySlug(m.cleanName) : undefined
        const id = slug ? categoryIdBySlug.get(slug) : undefined
        return id ? [[m.id, id]] : []
      }),
    ),
  }
}

/**
 * Compute which uncategorized transactions match the entity just linked to the
 * source transaction.
 *
 * Returns `{ matchedIds, pattern }`:
 *   - `matchedIds` — IDs of uncategorized rows (excluding the source) that the
 *     entity's alias picks up.
 *   - `pattern`    — The normalised alias pattern derived from the source description.
 */
export function computePropagation(input: RescanInput): RescanResult {
  return rescanUnmatchedForEntity(input)
}
